import { ApiClient, ApiError, type BranchComparison, type BranchSummary } from '../lib/api.js';
import { el } from '../lib/dom.js';
import { createBranchList } from './branch-list.js';

export interface BranchesViewDeps {
  api: ApiClient;
  onDeployRequested: (branch: string) => void;
}

const REF_DEBOUNCE_MS = 400;
const REF_OPTIONS_ID = 'branch-ref-options';

export function renderBranches(deps: BranchesViewDeps): HTMLElement {
  let protectedBranch = 'main';

  // Comparison state. `base` is what the server actually compared against —
  // an empty reference field means the protected branch, resolved server-side.
  let base = protectedBranch;
  let comparisons = new Map<string, BranchComparison>();
  let compareState: 'loading' | 'ready' | 'error' = 'loading';
  let compareError = '';
  // Guards against a slow response for an older reference overwriting a newer one.
  let compareToken = 0;
  let debounce: ReturnType<typeof setTimeout> | null = null;

  const mergeSelectedBtn = el('button', {
    class: 'primary',
    text: 'Merge to main',
    attrs: { disabled: true },
  });

  const refOptions = el('datalist', { attrs: { id: REF_OPTIONS_ID } });
  const refInput = el('input', {
    class: 'ref-input',
    attrs: {
      id: 'branch-ref-input',
      type: 'text',
      placeholder: 'main',
      list: REF_OPTIONS_ID,
      spellcheck: 'false',
      autocapitalize: 'off',
      autocomplete: 'off',
      'aria-label': 'Reference branch to compare against',
    },
  });
  const refStatus = el('span', { class: 'who' });

  const updateRefStatus = () => {
    if (compareState === 'loading') {
      refStatus.className = 'who';
      refStatus.textContent = 'Comparing…';
    } else if (compareState === 'error') {
      refStatus.className = 'who error';
      refStatus.textContent = compareError;
    } else {
      refStatus.className = 'who';
      refStatus.textContent = `↑ ahead of / ↓ behind "${base}"`;
    }
  };

  const badge = (text: string, kind: string, title: string): HTMLElement =>
    el('span', { class: `cmp-badge ${kind}`, text, attrs: { title } });

  const rowBadges = (b: BranchSummary): (HTMLElement | null)[] => {
    if (compareState === 'error') return [];
    if (compareState === 'loading') {
      return [badge('…', 'pending', `Comparing with "${base}"…`)];
    }
    const c = comparisons.get(b.name);
    if (!c) return [];
    if (c.error) return [badge('?', 'unknown', c.error)];
    if (b.name === base) return [badge('reference', 'reference', 'This is the reference branch')];
    if (c.ahead === 0 && c.behind === 0) {
      return [badge('in sync', 'sync', `Identical to "${base}"`)];
    }
    return [
      c.ahead > 0
        ? badge(
            `↑ ${c.ahead}`,
            'ahead',
            `${c.ahead} commit${c.ahead > 1 ? 's' : ''} ahead of "${base}" (not merged yet)`,
          )
        : null,
      c.behind > 0
        ? badge(
            `↓ ${c.behind}`,
            'behind',
            `${c.behind} commit${c.behind > 1 ? 's' : ''} behind "${base}" (missing from this branch)`,
          )
        : null,
    ];
  };

  const loadComparison = async () => {
    const ref = refInput.value.trim();
    const token = ++compareToken;
    compareState = 'loading';
    updateRefStatus();
    list.rerender();
    try {
      const data = await deps.api.compareBranches(ref || undefined);
      if (token !== compareToken) return;
      base = data.base;
      comparisons = new Map(data.comparisons.map((c) => [c.name, c]));
      compareState = 'ready';
    } catch (err) {
      if (token !== compareToken) return;
      comparisons = new Map();
      compareState = 'error';
      compareError =
        err instanceof ApiError
          ? err.status === 404
            ? `Reference branch "${ref}" not found`
            : err.message
          : 'Failed to compare branches';
    }
    updateRefStatus();
    list.rerender();
  };

  const scheduleComparison = () => {
    if (debounce) clearTimeout(debounce);
    debounce = setTimeout(() => void loadComparison(), REF_DEBOUNCE_MS);
  };

  refInput.addEventListener('input', scheduleComparison);
  refInput.addEventListener('change', () => {
    if (debounce) clearTimeout(debounce);
    void loadComparison();
  });
  refInput.addEventListener('keydown', (ev) => {
    if ((ev as KeyboardEvent).key !== 'Enter') return;
    if (debounce) clearTimeout(debounce);
    void loadComparison();
  });

  const list = createBranchList({
    title: 'Branches',
    source: {
      load: async () => {
        const data = await deps.api.listBranches();
        protectedBranch = data.protectedBranch;
        mergeSelectedBtn.textContent = `Merge to ${protectedBranch}`;
        if (refInput.value.trim() === '') base = protectedBranch;
        // Autocomplete for the reference field.
        refOptions.replaceChildren(
          ...data.branches.map((b) => el('option', { attrs: { value: b.name } })),
        );
        // Ahead/behind is a second round-trip; refresh it alongside the list.
        void loadComparison();
        return data;
      },
      delete: (branches) => deps.api.deleteBranches(branches),
    },
    slots: {
      headerControls: [
        el('label', { class: 'ref-label', text: 'Compare to', attrs: { for: 'branch-ref-input' } }),
        refInput,
        refOptions,
        refStatus,
      ],
      toolbarLeading: [mergeSelectedBtn],
      rowBadges,
      rowActions: (b) => [
        el('button', {
          text: 'Deploy',
          attrs: {
            title: `Run deployment commands using current ${protectedBranch} HEAD`,
          },
          on: { click: () => deps.onDeployRequested(b.name) },
        }),
      ],
    },
    onSelectionChange: (selected) => {
      mergeSelectedBtn.toggleAttribute('disabled', selected.length !== 1);
      mergeSelectedBtn.textContent =
        selected.length === 1
          ? `Merge "${selected[0]}" → ${protectedBranch}`
          : `Merge to ${protectedBranch}`;
    },
  });

  updateRefStatus();

  mergeSelectedBtn.addEventListener('click', async () => {
    const target = list.getSelected()[0];
    if (!target) return;
    if (!confirm(`Merge "${target}" into ${protectedBranch}?`)) return;
    mergeSelectedBtn.setAttribute('disabled', '');
    try {
      const result = await deps.api.mergeBranch(target);
      list.showAlert(
        result.status === 'already_merged'
          ? `"${target}" is already up to date with ${protectedBranch}`
          : `Merged "${target}" into ${protectedBranch}`,
        'success',
      );
      await list.refresh();
    } catch (err) {
      list.showAlert(err instanceof ApiError ? err.message : 'Merge failed');
    }
  });

  return el('div', { children: [list.root] });
}
